import { useState } from "react";
import { cn } from "../../lib/utils";
import type { ChapterVariation } from "../../types";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { EmptyState } from "../ui/empty-state";
import { Field } from "../ui/field";
import { SectionCard } from "../ui/section-card";
import { Spinner } from "../ui/spinner";
import { Textarea } from "../ui/textarea";

function formatVariationDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function statusLabel(status: ChapterVariation["status"]): string {
  if (status === "applied") return "Applied";
  if (status === "dismissed") return "Dismissed";
  return "Open";
}

function sourceHint(source: ChapterVariation["source"]): string | null {
  if (source === "storyCraft") return "From Story Craft checklist";
  return null;
}

function statusBadgeClass(status: ChapterVariation["status"]): string {
  if (status === "applied") return "bg-primary/15 text-primary";
  if (status === "dismissed") return "bg-muted text-muted-foreground";
  return "bg-secondary text-secondary-foreground";
}

type Props = {
  variations: ChapterVariation[];
  activeVariationId: string | null;
  onCompare: (variationId: string) => void;
  onDismiss: (variationId: string) => void;
  onCreateVariation: (instructions: string) => void;
  onStop: () => void;
  onSteer: (direction: string) => void;
  busy?: boolean;
};

export function ChapterVariationsPanel({
  variations,
  activeVariationId,
  onCompare,
  onDismiss,
  onCreateVariation,
  onStop,
  onSteer,
  busy = false,
}: Props) {
  const [instructions, setInstructions] = useState(
    "Generate a variation of this chapter. Preserve plot and POV; tighten prose and apply the master style guide."
  );
  const [steerDraft, setSteerDraft] = useState("");
  const sorted = [...variations].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <header>
        <h3 className="m-0 text-sm font-semibold text-foreground">Chapter variations</h3>
        <p className="mt-1 max-w-[70ch] text-xs leading-relaxed text-muted-foreground">
          Uses the Story Craft model (Actions tab). Start in Chat to clarify names and spelling, then click Generate
          variation. Or use New variation below — you can ask for formatting-only fixes (e.g. merge line breaks inside
          quoted dialogue).
        </p>
      </header>

      <Field label="Instructions for this variation">
        <Textarea
          aria-label="Variation instructions"
          className="min-h-22 text-[0.8125rem]"
          value={instructions}
          onChange={(event) => setInstructions(event.target.value)}
          disabled={busy}
        />
      </Field>
      <Button
        type="button"
        size="sm"
        variant={busy ? "outline" : "default"}
        className={cn(busy && "border-foreground/25")}
        onClick={busy ? onStop : () => onCreateVariation(instructions)}
        disabled={!busy && !instructions.trim()}
        aria-label={busy ? "Stop generating variation" : "Generate new variation"}
      >
        {busy ? (
          <>
            <span className="size-2.5 rounded-[2px] bg-current" aria-hidden />
            Stop generating
          </>
        ) : (
          "New variation"
        )}
      </Button>

      {busy ? (
        <div
          className="rounded-lg border border-primary/35 bg-primary/10 px-4 py-3.5"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-2.5">
            <Spinner aria-label="Generating variation" />
            <span className="text-[0.8125rem] font-semibold text-foreground">Generating variation…</span>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            Rewriting the chapter with your instructions. This can take a minute for long chapters.
          </p>
          <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-muted-foreground italic">
            {instructions.trim()}
          </p>
          <form
            className="mt-3 flex min-w-0 items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              const direction = steerDraft.trim();
              if (!direction) return;
              onSteer(direction);
              setSteerDraft("");
            }}
          >
            <Input
              aria-label="Steer variation generation"
              className="min-w-0 flex-1 bg-background"
              placeholder="Add direction while it works…"
              value={steerDraft}
              onChange={(event) => setSteerDraft(event.target.value)}
            />
            <Button type="submit" size="sm" disabled={!steerDraft.trim()}>
              Steer
            </Button>
          </form>
          <p className="mt-1.5 text-[0.68rem] leading-relaxed text-muted-foreground">
            Steering stops the current draft and restarts with your added direction.
          </p>
        </div>
      ) : null}

      {!busy && sorted.length === 0 ? (
        <EmptyState
          compact
          title="No variations yet for this chapter."
          description={
            <>
              Add instructions above, then click <strong>New variation</strong>. Or use{" "}
              <strong>Chat → Variation</strong> to discuss clarifications first, then{" "}
              <strong>Generate variation</strong> in the composer.
            </>
          }
        />
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
          {sorted.map((variation) => (
            <li key={variation.id}>
              <SectionCard
                className={cn(
                  activeVariationId === variation.id && "border-primary/60 ring-1 ring-primary/25"
                )}
                bodyClassName="flex items-start justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="mb-1.5 flex items-center gap-2">
                    <Badge
                      variant="secondary"
                      className={cn(
                        "h-5 px-2 text-[0.65rem] font-semibold tracking-wide uppercase",
                        statusBadgeClass(variation.status)
                      )}
                    >
                      {statusLabel(variation.status)}
                    </Badge>
                    <time className="text-[0.7rem] text-muted-foreground" dateTime={variation.createdAt}>
                      {formatVariationDate(variation.createdAt)}
                    </time>
                  </div>
                  <dl className="m-0 grid gap-1.5 text-[0.8125rem] leading-relaxed">
                    <div>
                      <dt className="inline font-semibold text-foreground">Reason: </dt>
                      <dd className="inline text-foreground">
                        {variation.reason || "Not recorded for this legacy variation"}
                      </dd>
                    </div>
                    <div>
                      <dt className="inline font-semibold text-muted-foreground">Instructions: </dt>
                      <dd className="inline text-muted-foreground">
                        {variation.prompt || "Chapter variation"}
                      </dd>
                    </div>
                  </dl>
                  {sourceHint(variation.source) ? (
                    <p className="mt-1 text-xs text-muted-foreground italic">{sourceHint(variation.source)}</p>
                  ) : null}
                  {variation.note ? <p className="mt-1 text-xs text-muted-foreground">{variation.note}</p> : null}
                  {variation.model ? <p className="mt-1 text-xs text-muted-foreground">{variation.model}</p> : null}
                </div>
                <div className="flex shrink-0 flex-col gap-1.5">
                  <Button type="button" size="sm" variant="outline" onClick={() => onCompare(variation.id)}>
                    Compare
                  </Button>
                  {variation.status === "open" ? (
                    <Button type="button" size="sm" variant="ghost" onClick={() => onDismiss(variation.id)}>
                      Dismiss
                    </Button>
                  ) : null}
                </div>
              </SectionCard>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
